import { useClerk, useUser } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { loadGeoSelection, type GeoSelection } from "../components/GeoSelector.js";
import { useState } from "react";

interface PageProps {
  devModeNoAuth?: boolean;
}

export function ProfilePage({ devModeNoAuth }: PageProps) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [geo] = useState<GeoSelection>(() => loadGeoSelection());
  const savedSignals = loadSavedSignals();
  const email = devModeNoAuth ? "dev@bios.local" : user?.primaryEmailAddress?.emailAddress ?? "BIOS user";
  const initials = email.slice(0, 1).toUpperCase();

  return (
    <div className="bios-profile-page">
      <header className="bios-profile-header">
        <div className="bios-profile-avatar">{initials}</div>
        <div>
          <div className="bios-profile-title">Profile</div>
          <p>{email}</p>
        </div>
      </header>

      <section className="bios-profile-list" aria-label="Profile settings">
        <ProfileRow label="Active geo" value={geoLabel(geo)} />
        <ProfileRow label="Install app" value="Add BIOS to your home screen from the browser menu." />
        <ProfileRow label="Notifications" value="Critical signal alerts pending setup." />
        <ProfileRow label="Saved / followed signals" value={`${savedSignals.length} saved`} />
      </section>

      <button
        type="button"
        className="bios-profile-signout"
        onClick={() => {
          if (devModeNoAuth) return;
          void signOut({ redirectUrl: "/" });
        }}
        disabled={devModeNoAuth}
      >
        Sign out
      </button>

      <ProfileBottomNav active="profile" />
      <div className="bios-feed-bottomnav-spacer" aria-hidden />
    </div>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="bios-profile-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ProfileBottomNav({ active }: { active: "signals" | "command" | "intel" | "search" | "profile" }) {
  return (
    <nav className="bios-feed-bottomnav" aria-label="Primary navigation">
      <Link to="/app" className={`bios-feed-bottomnav-tab${active === "signals" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◎</span><span className="bios-feed-bottomnav-label">Signals</span>
      </Link>
      <Link to="/app/command" className={`bios-feed-bottomnav-tab${active === "command" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌘</span><span className="bios-feed-bottomnav-label">Command</span>
      </Link>
      <Link to="/app/intel" className={`bios-feed-bottomnav-tab${active === "intel" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◐</span><span className="bios-feed-bottomnav-label">Intel</span>
      </Link>
      <Link to="/app/search" className={`bios-feed-bottomnav-tab${active === "search" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌕</span><span className="bios-feed-bottomnav-label">Search</span>
      </Link>
      <Link to="/app/profile" className={`bios-feed-bottomnav-tab${active === "profile" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◌</span><span className="bios-feed-bottomnav-label">Profile</span>
      </Link>
    </nav>
  );
}

function loadSavedSignals(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem("bios.savedSignals");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

const STATE_LABELS: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DC: "District of Columbia", DE: "Delaware",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

function geoLabel(geo: GeoSelection): string {
  if (geo.scope === "city" && geo.city) return geo.city;
  if (geo.scope === "state" && geo.state) return STATE_LABELS[geo.state] ?? geo.state;
  return "National";
}
